import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface PageEvent {
  pageIndex: number;
  previousPageIndex: number;
  pageSize: number;
  length: number;
}

const defaultPageSize = 50;

let nextUniqueId = 0;

@Component({
    selector: 'custom-paginator',
    templateUrl: './custom-paginator.component.html',
    styleUrls: ['./custom-paginator.component.scss'],
    standalone: false
})
export class CustomPaginatorComponent {
  @Input() pageIndex = 0;
  @Input() pageSizeOptions: number[] = [];

  @Input()
  get length(): number {
    return this._length;
  }
  set length(value: number) {
    this._length = Number.isFinite(value) ? value : 0;
  }
  private _length = 0;

  @Input()
  get pageSize(): number {
    return this._pageSize ?? this.pageSizeOptions[0] ?? defaultPageSize;
  }
  set pageSize(value: number) {
    this._pageSize = value;
  }
  private _pageSize: number = null;

  @Output() page = new EventEmitter<PageEvent>();

  readonly pageSizeLabelId = `custom-paginator-page-size-label-${nextUniqueId++}`;

  get displayedPageSizeOptions(): number[] {
    if (this.pageSizeOptions.includes(this.pageSize)) {
      return this.pageSizeOptions;
    }

    return [...this.pageSizeOptions, this.pageSize].sort((a, b) => a - b);
  }

  get rangeLabel(): string {
    if (this.length === 0 || this.pageSize === 0) {
      return `0 of ${this.length}`;
    }

    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex < this.length
      ? Math.min(startIndex + this.pageSize, this.length)
      : startIndex + this.pageSize;

    return `${startIndex + 1} – ${endIndex} of ${this.length}`;
  }

  getNumberOfPages(): number {
    if (!this.pageSize) {
      return 0;
    }

    return Math.ceil(this.length / this.pageSize);
  }

  hasPreviousPage(): boolean {
    return this.pageIndex >= 1 && this.pageSize !== 0;
  }

  hasNextPage(): boolean {
    return this.pageIndex < this.getNumberOfPages() - 1 && this.pageSize !== 0;
  }

  previousPage(): void {
    if (!this.hasPreviousPage()) {
      return;
    }

    this.navigate(this.pageIndex - 1);
  }

  nextPage(): void {
    if (!this.hasNextPage()) {
      return;
    }

    this.navigate(this.pageIndex + 1);
  }

  firstPage(): void {
    if (!this.hasPreviousPage()) {
      return;
    }

    this.navigate(0);
  }

  changePageSize(pageSize: number): void {
    const startIndex = this.pageIndex * this.pageSize;
    const previousPageIndex = this.pageIndex;

    this.pageIndex = Math.floor(startIndex / pageSize) || 0;
    this.pageSize = pageSize;
    this.emitPageEvent(previousPageIndex);
  }

  private navigate(pageIndex: number): void {
    const previousPageIndex = this.pageIndex;

    this.pageIndex = pageIndex;
    this.emitPageEvent(previousPageIndex);
  }

  private emitPageEvent(previousPageIndex: number): void {
    this.page.emit({
      pageIndex: this.pageIndex,
      previousPageIndex,
      pageSize: this.pageSize,
      length: this.length
    });
  }
}
